import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const TransactionHistory = ({ isOpen, onClose, onTransactionCancelled }) => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // Für Stornierlogik
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancellingTransaction, setCancellingTransaction] = useState(null);

  const loadTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Lade Transaktionshistorie (enthält jetzt auch all_transactions)
      const historyResponse = await apiService.getTransactionHistory();
      console.log('Transaction history:', historyResponse.data);
      setTransactions(historyResponse.data.transactions || []);
      setAllTransactions(historyResponse.data.all_transactions || []);
    } catch (error) {
      console.error('Transaction history Fehler:', error);
      setError(error.response?.data?.error || 'Fehler beim Laden der Transaktionshistorie');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTransactions();
    }
  }, [isOpen, loadTransactions]);

  const getAmount = (transaction) => {
    const amount = parseFloat(transaction.amount);
    if (transaction.type === 'recharge') {
      return `+${amount.toFixed(2)}€`;
    } else {
      return `-${amount.toFixed(2)}€`;
    }
  };

  const getTransactionIcon = (transaction) => {
    switch (transaction.type) {
      case 'recharge':
        return '💰';
      case 'gift_purchase':
        return '🎁';
      case 'purchase':
      default:
        return '🍺';
    }
  };

  const getTransactionTitle = (transaction) => {
    switch (transaction.type) {
      case 'recharge':
        return 'Guthaben aufgeladen';
      case 'gift_purchase':
        // Verwende die description vom Backend, da sie bereits die korrekte Perspektive hat
        return transaction.description || `${transaction.drink_name} gekauft`;
      case 'purchase':
      default:
        return transaction.description || `${transaction.drink_name} gekauft`;
    }
  };

  const getTransactionColor = (transaction) => {
    switch (transaction.type) {
      case 'recharge':
        return 'text-green-600';
      case 'gift_purchase':
        return 'text-beer-600';
      case 'purchase':
      default:
        return 'text-red-600';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canCancelTransaction = (transaction) => {
    // Nur Getränk-Transaktionen können storniert werden
    if (!['purchase', 'gift_purchase'].includes(transaction.type)) return false;
    
    // Nur Transaktionen der letzten 24 Stunden
    const transactionDate = new Date(transaction.timestamp);
    const now = new Date();
    const hoursDiff = (now - transactionDate) / (1000 * 60 * 60);
    
    if (hoursDiff > 24) return false;
    
    // Alle Getränk-Transaktionen der letzten 24 Stunden finden und sortieren
    // Verwende allTransactions für die Stornierlogik (inklusive stornierte)
    const allDrinkTransactions = allTransactions.filter(t => {
      if (!['purchase', 'gift_purchase'].includes(t.type)) return false;
      const tDate = new Date(t.timestamp);
      const tHoursDiff = (now - tDate) / (1000 * 60 * 60);
      return tHoursDiff <= 24;
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Neueste zuerst
    
    // Nur die ersten 3 (neuesten) sind stornierbar
    const cancellableTransactions = allDrinkTransactions.slice(0, 3);
    
    // Prüfen ob diese Transaktion in den stornierbaren ist
    const isCancellable = cancellableTransactions.some(t => t.id === transaction.id);
    
    return isCancellable;
  };

  const handleCancelTransaction = async (transactionId) => {
    if (!window.confirm('Möchtest du diese Transaktion wirklich stornieren?')) return;
    
    setCancellingTransaction(transactionId);
    try {
      const response = await apiService.cancelOwnTransaction(transactionId);
      if (response.data.success) {
        // Transaktionen neu laden (stornierte werden nicht mehr angezeigt)
        await loadTransactions();
        
        // Callback aufrufen um das Guthaben zu aktualisieren
        if (onTransactionCancelled) {
          onTransactionCancelled(response.data.new_balance);
        }
        
        setError(null);
      }
    } catch (error) {
      console.error('Fehler beim Stornieren der Transaktion:', error);
      setError(error.response?.data?.error || 'Fehler beim Stornieren der Transaktion');
    } finally {
      setCancellingTransaction(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">📜</div>
          <h2 className="text-2xl font-bold text-wood-800 mb-2">Transaktionshistorie</h2>
          <p className="text-wood-600 text-sm">Deine Getränke- und Guthaben-Historie</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-beer-500"></div>
              <span className="ml-2 text-wood-600">Lade Transaktionen...</span>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-wood-500">Keine Transaktionen gefunden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="bg-wood-100 rounded-lg p-3 border border-wood-200 hover:bg-wood-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <span className="text-xl">{getTransactionIcon(transaction)}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-wood-800 text-sm truncate">
                          {getTransactionTitle(transaction)}
                        </h3>
                        <p className="text-xs text-wood-600">{formatDate(transaction.timestamp)}</p>
                      </div>
                    </div>
                    <div className="text-right ml-2 flex items-center space-x-2">
                      <div>
                        <p className={`font-bold text-base ${getTransactionColor(transaction)}`}>
                          {getAmount(transaction)}
                        </p>
                        {transaction.type === 'gift_purchase' && (
                          <p className="text-xs text-wood-600">
                            von {transaction.paid_by_name}
                          </p>
                        )}
                      </div>
                      {canCancelTransaction(transaction) && (
                        <button
                          onClick={() => handleCancelTransaction(transaction.id)}
                          disabled={cancellingTransaction === transaction.id}
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded text-xs font-medium transition-colors duration-200 disabled:opacity-50"
                        >
                          {cancellingTransaction === transaction.id ? '...' : '❌'}
                        </button>
                      )}
                    </div>
                  </div>

                  {transaction.type === 'gift_purchase' && (
                    <div className="bg-beer-50 rounded p-2 mt-2 border border-beer-200">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-wood-700">
                          <strong>Geschenk:</strong> {transaction.gift_type === 'next_round' ? 'Nächste Runde' : 'Ganzer Abend'}
                        </span>
                        <span className="text-wood-600">von {transaction.gift_giver_name}</span>
                      </div>
                      {transaction.gift_message && (
                        <p className="text-xs text-wood-700 mt-1 italic">
                          "{transaction.gift_message}"
                        </p>
                      )}
                    </div>
                  )}

                  {transaction.description && (
                    <p className="text-xs text-wood-600 mt-1">{transaction.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-wood-200">
          <button
            onClick={onClose}
            className="w-full bg-wood-200 hover:bg-wood-300 text-wood-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
